import { DayOfMonthLogic } from '../../../../uicomponents/src/date-picker/day-of-month.logic';
import { MonthOfYearLogic } from '../../../../uicomponents/src/date-picker/month-of-year.logic';
import { YearLogic } from '../../../../uicomponents/src/date-picker/year.logic';
import { DatePickerClasses } from '../../../../uicomponents/src/date-picker/date-picker-classes';
import { FormField } from './form-field';

// Extend the global Window interface to declare the DobPicker property
declare global {
    interface Window {
        DobPicker: {
            DobCompleteEventName: string;
            ValidityChangeEventName: string;
        };
    }
}

// Declare the htmx global
declare const htmx: {
    addClass: (element: HTMLElement, className: string) => void;
    removeClass: (element: HTMLElement, className: string) => void;
    on: (event: string, callback: (event: any) => void) => void;
};

enum DOBFeedbackMessageIds {
    DOBFormatInvalid = 'dobFormatInvalid',
    DOBInFuture = 'dobInFuture',
    DOBRequired = 'dobRequired',
    InvalidDay = 'invalidDay',
    InvalidMonth = 'invalidMonth',
    InvalidYear = 'invalidYear'
}

class DobPicker {
    static ValidityChangeEventName: string = 'validitychange';
    static DobCompleteEventName: string = 'dobComplete';

    private dobErrors: Record<DOBFeedbackMessageIds, boolean> = {
        [DOBFeedbackMessageIds.DOBFormatInvalid]: false,
        [DOBFeedbackMessageIds.DOBInFuture]: false,
        [DOBFeedbackMessageIds.DOBRequired]: false,
        [DOBFeedbackMessageIds.InvalidDay]: false,
        [DOBFeedbackMessageIds.InvalidMonth]: false,
        [DOBFeedbackMessageIds.InvalidYear]: false
    };

    private get noDateError(): boolean {
        return Object.values(this.dobErrors).every(
            (inError: boolean) => !inError
        );
    }

    constructor(
        private readonly datePickerContainer: HTMLDivElement | null,
        private readonly dobGroup: HTMLDivElement,
        private readonly dayOfMonth: FormField,
        private readonly monthOfYear: FormField,
        private readonly year: FormField,
        private readonly feedbackMessages: HTMLDivElement
    ) {
        this.initDOBGroup();
        this.updateFeedbackMessage();
    }

    private getFeedbackMessage(
        dobFeedbackMessageId: DOBFeedbackMessageIds
    ): string | null | undefined {
        return this.feedbackMessages.children.namedItem(dobFeedbackMessageId)
            ?.textContent;
    }

    private initDOBGroup() {
        const groupInputs: NodeListOf<HTMLInputElement> =
            this.dobGroup.querySelectorAll('input');

        groupInputs.forEach((input: HTMLInputElement) => {
            input.addEventListener('blur', () => {
                // Set the input as touched
                htmx.addClass(input, DatePickerClasses.IsTouched);

                // Clear all field validity values
                this.resetFieldsetValidity();

                // Validate the fulldate
                this.validateDate();
            });

            // Allow only numbers
            input.addEventListener('keypress', (event: KeyboardEvent) => {
                if (event.key !== 'Backspace' && event.key !== 'Delete') {
                    if (!/^\d$/.test(event.key)) {
                        event.preventDefault();
                    }
                }
            });
        });

        if (
            !this.monthOfYear.isTouched &&
            !this.dayOfMonth.isTouched &&
            !this.year.isTouched &&
            (!this.monthOfYear.base.validity.valueMissing ||
                !this.dayOfMonth.base.validity.valueMissing ||
                !this.year.base.validity.valueMissing)
        ) {
            // Set the inputs as touched so future input specific validation will trigger
            this.monthOfYear.setTouched();
            this.dayOfMonth.setTouched();
            this.year.setTouched();

            this.validatePopulatedDateFields();
            this.updateFeedbackMessage();
        }
    }

    private resetFieldsetValidity() {
        this.dobErrors[DOBFeedbackMessageIds.InvalidDay] = false;
        this.dayOfMonth.setValidity(true);
        this.dobErrors[DOBFeedbackMessageIds.InvalidMonth] = false;
        this.monthOfYear.setValidity(true);
        this.dobErrors[DOBFeedbackMessageIds.InvalidYear] = false;
        this.year.setValidity(true);
    }

    private setFieldsetInvalid() {
        const dobInvalidMessage: string | null | undefined =
            this.getFeedbackMessage(DOBFeedbackMessageIds.DOBInFuture);

        this.dayOfMonth.setValidity(false, dobInvalidMessage ?? '');
        this.monthOfYear.setValidity(false, dobInvalidMessage ?? '');
        this.year.setValidity(false, dobInvalidMessage ?? '');
    }

    private updateFeedbackMessage(): void {
        Array.from(this.feedbackMessages.children).forEach(
            (feedbackMessage: Element) => {
                const feedbackMessageElem: HTMLElement =
                    feedbackMessage as HTMLElement;
                feedbackMessageElem.hidden = true;
            }
        );

        // Dispatch validitychange event to the top level element of this "component"
        this.datePickerContainer?.dispatchEvent(
            new CustomEvent(DobPicker.ValidityChangeEventName, {
                detail: { isValid: this.noDateError }
            } as CustomEventInit)
        );

        // If DOB is valid and complete, trigger a dobComplete event
        if (
            this.noDateError &&
            this.monthOfYear.base.value &&
            this.dayOfMonth.base.value &&
            this.year.base.value
        ) {
            this.datePickerContainer?.dispatchEvent(
                new CustomEvent(DobPicker.DobCompleteEventName, {
                    detail: {
                        month: this.monthOfYear.base.value,
                        day: this.dayOfMonth.base.value,
                        year: this.year.base.value
                    }
                } as CustomEventInit)
            );
        }

        if (this.dobErrors[DOBFeedbackMessageIds.DOBRequired]) {
            this.feedbackMessages.children
                .namedItem(DOBFeedbackMessageIds.DOBRequired)
                ?.removeAttribute('hidden');
            return;
        } else {
            this.feedbackMessages.children
                .namedItem(DOBFeedbackMessageIds.DOBRequired)
                ?.setAttribute('hidden', 'true');
        }

        if (this.dobErrors[DOBFeedbackMessageIds.DOBFormatInvalid]) {
            this.feedbackMessages.children
                .namedItem(DOBFeedbackMessageIds.DOBFormatInvalid)
                ?.removeAttribute('hidden');
            return;
        } else {
            this.feedbackMessages.children
                .namedItem(DOBFeedbackMessageIds.DOBFormatInvalid)
                ?.setAttribute('hidden', 'true');
        }

        if (this.dobErrors[DOBFeedbackMessageIds.DOBInFuture]) {
            this.feedbackMessages.children
                .namedItem(DOBFeedbackMessageIds.DOBInFuture)
                ?.removeAttribute('hidden');
            return;
        } else {
            this.feedbackMessages.children
                .namedItem(DOBFeedbackMessageIds.DOBInFuture)
                ?.setAttribute('hidden', 'true');
        }

        if (this.dobErrors[DOBFeedbackMessageIds.InvalidMonth]) {
            this.feedbackMessages.children
                .namedItem(DOBFeedbackMessageIds.InvalidMonth)
                ?.removeAttribute('hidden');
            return;
        } else {
            this.feedbackMessages.children
                .namedItem(DOBFeedbackMessageIds.InvalidMonth)
                ?.setAttribute('hidden', 'true');
        }

        if (this.dobErrors[DOBFeedbackMessageIds.InvalidDay]) {
            this.feedbackMessages.children
                .namedItem(DOBFeedbackMessageIds.InvalidDay)
                ?.removeAttribute('hidden');
            return;
        } else {
            this.feedbackMessages.children
                .namedItem(DOBFeedbackMessageIds.InvalidDay)
                ?.setAttribute('hidden', 'true');
        }

        if (this.dobErrors[DOBFeedbackMessageIds.InvalidYear]) {
            this.feedbackMessages.children
                .namedItem(DOBFeedbackMessageIds.InvalidYear)
                ?.removeAttribute('hidden');
            return;
        } else {
            this.feedbackMessages.children
                .namedItem(DOBFeedbackMessageIds.InvalidYear)
                ?.setAttribute('hidden', 'true');
        }
    }

    private validateDate(): void {
        if (
            this.monthOfYear.isTouched &&
            this.dayOfMonth.isTouched &&
            this.year.isTouched
        ) {
            if (
                this.monthOfYear.base.validity.valueMissing ||
                this.dayOfMonth.base.validity.valueMissing ||
                this.year.base.validity.valueMissing
            ) {
                this.dobErrors[DOBFeedbackMessageIds.DOBRequired] = true;
            } else {
                this.validatePopulatedDateFields();
            }
        } else {
            if (this.monthOfYear.isTouched) {
                this.validateMonthOfYear();
            }

            if (this.dayOfMonth.isTouched) {
                this.validateDayOfMonth();
            }

            if (this.year.isTouched) {
                this.validateYear();
            }
        }

        this.updateFeedbackMessage();
    }

    private validateDayOfMonth(): boolean {
        const dayOfMonth: number = Number(this.dayOfMonth.base.value);
        const monthOfYear: number = Number(this.monthOfYear.base.value);
        const year: number = Number(this.year.base.value);

        // Don't validate if the field is not touched
        if (!this.dayOfMonth.isTouched) {
            return true;
        }

        if (!dayOfMonth) {
            const invalidDay: string | null | undefined =
                this.getFeedbackMessage(DOBFeedbackMessageIds.InvalidDay);
            this.dayOfMonth.setValidity(false, invalidDay ?? '');

            this.dobErrors[DOBFeedbackMessageIds.InvalidDay] = true;

            return false;
        } else {
            this.dobErrors[DOBFeedbackMessageIds.InvalidDay] = false;
        }

        const dayIsValid: boolean =
            Boolean(dayOfMonth) &&
            DayOfMonthLogic.isDayValid(dayOfMonth, monthOfYear, year);

        if (
            dayIsValid &&
            DayOfMonthLogic.isDayLeadingZeroFormatted(
                this.dayOfMonth.base.value
            )
        ) {
            this.dayOfMonth.setValidity(true);
            this.dobErrors[DOBFeedbackMessageIds.InvalidDay] = false;
        } else {
            const invalidDay: string | null | undefined =
                this.getFeedbackMessage(DOBFeedbackMessageIds.InvalidDay);
            this.dayOfMonth.setValidity(false, invalidDay ?? '');

            this.dobErrors[DOBFeedbackMessageIds.InvalidDay] = true;
        }

        return dayIsValid;
    }

    private validateMonthOfYear(): boolean {
        const monthOfYear: number = Number(this.monthOfYear.base.value);

        if (
            monthOfYear &&
            MonthOfYearLogic.isMonthValid(monthOfYear) &&
            MonthOfYearLogic.isMonthLeadingZeroFormatted(
                this.monthOfYear.base.value
            )
        ) {
            this.monthOfYear.setValidity(true);
            this.dobErrors[DOBFeedbackMessageIds.InvalidMonth] = false;
        } else {
            const invalidMonth: string | null | undefined =
                this.getFeedbackMessage(DOBFeedbackMessageIds.InvalidMonth);
            this.monthOfYear.setValidity(false, invalidMonth ?? '');

            this.dobErrors[DOBFeedbackMessageIds.InvalidMonth] = true;
        }

        return !this.dobErrors[DOBFeedbackMessageIds.InvalidMonth];
    }

    private validateYear(): boolean {
        const year: number = Number(this.year.base.value);

        if (
            year &&
            YearLogic.isYearValid(year, 1900) &&
            YearLogic.isYearFourDigitFormatted(this.year.base.value)
        ) {
            this.year.setValidity(true);
            this.dobErrors[DOBFeedbackMessageIds.InvalidYear] = false;
        } else {
            const invalidYear: string | null | undefined =
                this.getFeedbackMessage(DOBFeedbackMessageIds.InvalidYear);
            this.year.setValidity(false, invalidYear ?? '');

            this.dobErrors[DOBFeedbackMessageIds.InvalidYear] = true;
        }

        return !this.dobErrors[DOBFeedbackMessageIds.InvalidMonth];
    }

    private validatePopulatedDateFields() {
        this.dobErrors[DOBFeedbackMessageIds.DOBRequired] = false;

        const dateString: string = `${this.monthOfYear.base.value}/${this.dayOfMonth.base.value}/${this.year.base.value}`;

        if (
            this.validateMonthOfYear() &&
            this.validateYear() &&
            this.validateDayOfMonth()
        ) {
            if (
                dateString.match(
                    /^(0[1-9]|1[012])\/(0[1-9]|[12][0-9]|3[01])\/(\d{4})$/
                )
            ) {
                this.dobErrors[DOBFeedbackMessageIds.DOBFormatInvalid] = false;

                const dob: Date = new Date(dateString);
                const beginningOfDay: Date = new Date();
                const currentDOBInvalid: boolean =
                    this.dobErrors[DOBFeedbackMessageIds.DOBInFuture];

                beginningOfDay.setHours(0, 0, 0);
                this.dobErrors[DOBFeedbackMessageIds.DOBInFuture] =
                    beginningOfDay.valueOf() <= dob.valueOf();

                if (
                    currentDOBInvalid !==
                    this.dobErrors[DOBFeedbackMessageIds.DOBInFuture]
                ) {
                    this.resetFieldsetValidity();
                }
            } else {
                this.dobErrors[DOBFeedbackMessageIds.DOBFormatInvalid] = true;
            }
        } else {
            this.dobErrors[DOBFeedbackMessageIds.DOBFormatInvalid] = false;
            this.dobErrors[DOBFeedbackMessageIds.DOBInFuture] = false;
        }

        if (
            this.dobErrors[DOBFeedbackMessageIds.DOBInFuture] ||
            this.dobErrors[DOBFeedbackMessageIds.DOBFormatInvalid]
        ) {
            this.setFieldsetInvalid();
        }
    }
}

// Binds the page's DOB picker once. A form swapped back in by htmx brings a new
// picker, which the guard lets through.
export function initDobPicker(): void {
    const datePickerContainer: HTMLDivElement | null =
        document.querySelector('#dobPicker');
    if (!datePickerContainer || datePickerContainer.dataset.dobBound) return;
    const dayOfMonth: FormField = new FormField(
        document.querySelector('#dobDay') as HTMLInputElement
    );
    const monthOfYear: FormField = new FormField(
        document.querySelector('#dobMonth') as HTMLInputElement
    );
    const year: FormField = new FormField(
        document.querySelector('#dobYear') as HTMLInputElement
    );
    const dobGroup: HTMLDivElement = document.querySelector(
        '#dob'
    ) as HTMLDivElement;
    const dobFeedbackMessage: HTMLDivElement = document.querySelector(
        '#dobFeedbackMessage'
    ) as HTMLDivElement;

    if (dayOfMonth && monthOfYear && year && dobGroup && dobFeedbackMessage) {
        new DobPicker(
            datePickerContainer,
            dobGroup,
            dayOfMonth,
            monthOfYear,
            year,
            dobFeedbackMessage
        );
        datePickerContainer.dataset.dobBound = 'true';
    }
}

document.addEventListener('DOMContentLoaded', () => initDobPicker());
document.addEventListener('htmx:afterSettle', () => initDobPicker());

// Expose DobPicker event names globally to avoid magic strings in other files
window.DobPicker = {
    DobCompleteEventName: DobPicker.DobCompleteEventName,
    ValidityChangeEventName: DobPicker.ValidityChangeEventName
};
